import React, { useEffect, useState } from 'react';
import styles from './enrichment-name-server-information.module.sass';
import { Button, Typography, Tooltip, Table } from 'antd';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import ProgressBar from '../../../../shared-components/ProgressBar';
import LoadingSkeleton from '../../../../shared-components/LoadingSkeleton';
import { InfoCircleFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';

function EnrichmentNameServerInformation({
  history,
  newData,
  statusNew,
  props,
}) {
  const { Title, Text, Paragraph } = Typography;
  const [originalTableData, setOriginalTableData] = useState([]);
  const [slicedTable, setSlicedTable] = useState([]);
  const [showButtonText, setShowButtonText] = useState('Show More');
  const [tableOverflow, setTableOverflow] = useState(false);
  const nameServerInfo = newData?.response;

  function showMore() {
    if (tableOverflow) {
      setShowButtonText('Show Less');
      setTableOverflow(false);
    } else {
      setShowButtonText('Show More');
      setTableOverflow(true);
    }
  }

  function handleLookup(name) {
    history.push(
      `/explore-result?name=${name}&queryType=answer&type=NS&backLink=` +
        encodeURIComponent(window.location.pathname) +
        encodeURIComponent(window.location.search) +
        '&origin_uid=Explore',
    );
  }

  function returnLoadingState() {
    return [
      {
        key: 1,
        name: <LoadingSkeleton />,
        value: <LoadingSkeleton />,
        padn: null,
        serverName: null,
      },
      {
        key: 2,
        name: (
          <div className={styles.loading_container}>
            <LoadingSkeleton />
            <LoadingSkeleton />
            <LoadingSkeleton />
            <LoadingSkeleton />
          </div>
        ),
        value: (
          <div className={styles.loading_container}>
            <LoadingSkeleton />
            <LoadingSkeleton />
            <LoadingSkeleton />
            <LoadingSkeleton />
          </div>
        ),
        padn: null,
        serverName: null,
      },
    ];
  }

  function renderNameServerInformation(data, i) {
    let nameServer = {
      key: null,
      name: null,
      value: null,
      padn: null,
      serverName: null,
      tooltip: null,
    };

    nameServer.key = Number(i) + 2;
    nameServer.name = (
      <div className={styles.names}>
        <Text>Name Server</Text>
        <Text>
          NS domain density{' '}
          <Tooltip title="How many domains are used by a specific nameserver">
            <InfoCircleFilled
              style={{ display: 'inline', verticalAlign: 'middle' }}
            />
          </Tooltip>
        </Text>
        <Text>
          NS domain listed{' '}
          <Tooltip title="The number of domains using this name server are found on feeds and/or blacklists">
            <InfoCircleFilled
              style={{ display: 'inline', verticalAlign: 'middle' }}
            />
          </Tooltip>
        </Text>
        <Text>NS reputation</Text>
      </div>
    );
    nameServer.value = (
      <div className={styles.values}>
        <Text strong>{data?.ns_server}</Text>
        <Text strong>{data?.ns_server_domain_density}</Text>
        <Text strong>{data?.ns_server_domains_listed}</Text>
        <Text strong>{data?.ns_server_reputation}</Text>
      </div>
    );
    nameServer.padn = true;
    nameServer.serverName = data?.ns_server;

    return nameServer;
  }

  const columns = [
    {
      title: <ColumnTitle columnName="Name" />,
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: <ColumnTitle columnName="Value" />,
      dataIndex: 'value',
      key: 'value',
    },
    {
      dataIndex: 'padn',
      key: 'padn',
      with: 100,
      className: styles.button_column,
      render: (value, param) => {
        if (value) {
          return props?.mySubscriptionPlan?.has_access_to_explore ? (
            <Link
              to={
                `/explore-result?name=${param?.serverName}&queryType=answer&type=NS&backLink=` +
                encodeURIComponent(window.location.pathname) +
                encodeURIComponent(window.location.search) +
                '&origin_uid=Explore'
              }
            >
              <Button
                type="primary"
                //onClick={() => handleLookup(param?.serverName)}
              >
                Lookup PADNS
              </Button>
            </Link>
          ) : (
            <Tooltip
              title={
                props?.mySubscriptionPlan?.has_access_to_explore ? (
                  ''
                ) : (
                  <>
                    This option is not part of this subscription plan. To
                    upgrade your subscription contact us at{' '}
                    <a target="_blank" href="mailto:sales@silentpush.com">
                      sales@silentpush.com
                    </a>
                    .
                  </>
                )
              }
            >
              <Button
                disabled
                type="primary"
                // onClick={() => handleLookup(param?.serverName)}
              >
                Lookup PADNS
              </Button>
            </Tooltip>
          );
        } else {
          return null;
        }
      },
    },
  ];

  const initialTableData = [
    {
      key: 1,
      name: (
        <Text>
          NS Reputation{' '}
          <Tooltip title="The ratio of blacklisted domains, taken from the total number of domains using a nameserver">
            <InfoCircleFilled />
          </Tooltip>
        </Text>
      ),
      value: (
        <div className={styles.reputation}>
          <Text strong>
            {nameServerInfo?.ns_reputation?.ns_reputation_max ||
            nameServerInfo?.ns_reputation?.ns_reputation_max == 0
              ? nameServerInfo?.ns_reputation?.ns_reputation_max
              : '-'}
          </Text>
          <ProgressBar
            bgcolor={nameServerInfo?.ns_reputation?.ns_reputation_score}
            width={'72'}
            completed={nameServerInfo?.ns_reputation?.ns_reputation_score}
          />
        </div>
      ),
      padn: null,
      serverName: null,
    },
  ];

  useEffect(() => {
    if (statusNew === 'loaded') {
      setTableOverflow(false);
      setSlicedTable([]);
      const srvReputation = nameServerInfo?.ns_reputation?.ns_srv_reputation;

      for (let i in srvReputation) {
        initialTableData.push(
          renderNameServerInformation(srvReputation?.[i], i),
        );
      }
      setOriginalTableData(initialTableData);

      if (initialTableData?.length > 0 && !(initialTableData?.length < 4)) {
        setTableOverflow(true);
        setSlicedTable(initialTableData?.slice(0, 4));
      }
    }
  }, [nameServerInfo]);

  if (statusNew === 'loaded') {
    return (
      <div
        id="enrichmentNameServerInformation"
        className={styles.enrichment_name_server_information}
      >
        <div className={styles.title}>
          <Title level={4}>Name Server Information</Title>
          <Text>
            Information about Domain's Name Servers.&nbsp;
            <Text strong>Silent Push invention</Text>
          </Text>
        </div>

        <Table
          columns={columns}
          pagination={false}
          dataSource={tableOverflow ? slicedTable : originalTableData}
        />
        <Button
          className={styles.show_more}
          onClick={() => showMore()}
          disabled={!(slicedTable?.length > 0)}
          type="default"
        >
          {showButtonText}
        </Button>
      </div>
    );
  } else {
    return (
      <div className={styles.enrichment_name_server_information}>
        <div className={styles.title}>
          <Title level={4}>Name Server Information</Title>
          <Text>
            Information about Domain's Name Servers.&nbsp;
            <Text strong>Silent Push invention</Text>
          </Text>
        </div>

        <Table
          columns={columns}
          pagination={false}
          dataSource={returnLoadingState()}
        />
      </div>
    );
  }
}

export default EnrichmentNameServerInformation;
